import { type HistoryEntityType, type HistoryEntry } from "@eisox/api/core-api";
import { useQueries, type QueryClient } from "@tanstack/react-query";

import { historyApi } from "~/api";

export interface HistorySeriesRequest {
  entityId: string;
  point: string;
}

interface UseBuildingHistoryParams {
  buildingId: string;
  startDate: string;
  endDate: string;
  entityType: HistoryEntityType;
  series: HistorySeriesRequest[];
  enabled?: boolean;
}

interface UseBuildingHistoryResult {
  /** Flattened entries once every series has loaded all pages; undefined while loading. */
  data: HistoryEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Fetches paginated history for each entity/point pair; exposes data only when all series are complete. */
export const useBuildingHistory = ({
  buildingId,
  startDate,
  endDate,
  entityType,
  series,
  enabled = true,
}: UseBuildingHistoryParams): UseBuildingHistoryResult => {
  const results = useQueries({
    queries: series.map(({ entityId, point }) => ({
      ...historyApi.queries.building(buildingId, {
        startDate,
        endDate,
        entityId,
        point,
        entityType,
      }),
      enabled: enabled && series.length > 0,
      meta: { skipGlobalLoader: true },
    })),
    combine: queries => {
      if (!enabled || series.length === 0) {
        return { data: undefined, isLoading: false, isError: false };
      }

      const isLoading = queries.some(query => query.isLoading || query.isFetching);
      const isError = queries.some(query => query.isError);
      const allSuccess = queries.every(query => query.isSuccess);

      return {
        data: allSuccess ? queries.flatMap(query => query.data) : undefined,
        isLoading: isLoading || !allSuccess,
        isError,
      };
    },
  });

  return results;
};

/** Fetches all pages for each series in parallel (e.g. export on submit). */
export const fetchBuildingHistory = async (
  queryClient: QueryClient,
  buildingId: string,
  startDate: string,
  endDate: string,
  entityType: HistoryEntityType,
  series: HistorySeriesRequest[],
): Promise<HistoryEntry[]> => {
  const pages = await Promise.all(
    series.map(({ entityId, point }) =>
      queryClient.fetchQuery({
        ...historyApi.queries.building(buildingId, {
          startDate,
          endDate,
          entityId,
          point,
          entityType,
        }),
      }),
    ),
  );

  return pages.flat();
};
